import { useCallback, useEffect, useMemo, useState } from "react";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  Alert,
  Button,
  Dialog,
  EmptyState,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { LoadBoundary, Panel, useHubLoad } from "@/modules/hub-kit/HubKit";
import { formatCount, formatDateTime } from "@/modules/hub-kit/format";
import { createGrowthApi, type VoucherDto, type VoucherRedemptionDto } from "../growthApi";

/**
 * Promo codes: the HQ RECORD (read-only).
 *
 * HUDDL issues no promo codes. A business creates its own in its Business
 * Manager (Menu, Marketing) and pays for the discount. HQ keeps the record of
 * every code and every use, and can neither create, edit, switch on nor switch
 * off one. There is deliberately no button for any of that here.
 */

const TONE: Record<VoucherDto["status"], "success" | "neutral" | "warning" | "danger"> = {
  live: "success",
  inactive: "neutral",
  scheduled: "neutral",
  expired: "warning",
  exhausted: "warning",
};

const STATUS_LABEL: Record<VoucherDto["status"], string> = {
  live: "Live",
  inactive: "Switched off",
  scheduled: "Not started",
  expired: "Expired",
  exhausted: "Used up",
};

const REDEMPTION_LABEL: Record<VoucherRedemptionDto["status"], string> = {
  consumed: "Used",
  reserved: "In checkout",
  released: "Given back",
};

const REDEMPTION_TONE: Record<VoucherRedemptionDto["status"], "success" | "neutral" | "warning"> = {
  consumed: "success",
  reserved: "warning",
  released: "neutral",
};

const pesos = (n: number | string) => `₱${Number(n).toLocaleString("en-PH", { maximumFractionDigits: 2 })}`;

function describe(v: VoucherDto): string {
  const parts = [v.percent_off !== null ? `${v.percent_off}% off` : `${pesos(v.amount_off ?? 0)} off`];
  if (v.max_discount !== null) parts.push(`up to ${pesos(v.max_discount)}`);
  if (v.min_spend > 0) parts.push(`min spend ${pesos(v.min_spend)}`);
  return parts.join(", ");
}

export function PromoCodesTab() {
  const client = useApiClient();
  const api = useMemo(() => createGrowthApi(client), [client]);
  const load = useCallback(async () => (await api.listVouchers()).vouchers, [api]);
  const { data, error, loading, reload } = useHubLoad(load);
  const [open, setOpen] = useState<VoucherDto | null>(null);

  return (
    <Stack gap={16}>
      <Alert tone="info" title="Promo codes are issued and paid for by businesses">
        HUDDL does not create promo codes. A venue or freelancer makes its own in its Business Manager, under Menu, Marketing,
        and it works only on that business's own bookings. The discount comes out of the business's payout, and the platform
        fee is still charged on the original price. This page is the record: every code with its terms, and every use with
        who, which booking and how much. It is read-only.
      </Alert>
      <Flex gap={8} justify="space-between" align="center" wrap>
        <Text role="body" color="secondary" as="p">
          A use counts only once its booking is confirmed. If that booking is cancelled or fully refunded, the use is given
          back and shows as "Given back".
        </Text>
        <Button variant="secondary" onClick={() => void reload()}>
          Refresh
        </Button>
      </Flex>
      <LoadBoundary loading={loading} error={error} onRetry={() => void reload()} hasData={!!data} what="promo codes">
        {data && data.length === 0 ? (
          <EmptyState
            title="No promo codes yet"
            description="Nothing to record until a business creates a code in its Business Manager."
          />
        ) : null}
        {data && data.length > 0 ? (
          <Panel title={`${formatCount(data.length)} promo code${data.length === 1 ? "" : "s"}`}>
            <Table>
              <Thead>
                <Tr>
                  <Th>Code</Th>
                  <Th>Business</Th>
                  <Th>Offer</Th>
                  <Th>Status</Th>
                  <Th>Used</Th>
                  <Th>Expires</Th>
                  <Th>Record</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.map((v) => (
                  <Tr key={v.id}>
                    <Td>
                      <Stack gap={4}>
                        <strong>{v.code}</strong>
                        {v.label ? (
                          <Text role="caption" color="muted" as="span">
                            {v.label}
                          </Text>
                        ) : null}
                      </Stack>
                    </Td>
                    <Td>
                      {v.business_name ? (
                        v.business_name
                      ) : (
                        <Text role="caption" color="muted" as="span">
                          No business, cannot be redeemed
                        </Text>
                      )}
                    </Td>
                    <Td>{describe(v)}</Td>
                    <Td>
                      <StatusChip tone={TONE[v.status]}>{STATUS_LABEL[v.status]}</StatusChip>
                    </Td>
                    <Td>
                      {formatCount(v.used_count)}
                      {v.max_uses !== null ? ` of ${formatCount(v.max_uses)}` : ""}
                    </Td>
                    <Td>{v.expires_at ? formatDateTime(v.expires_at) : "Never"}</Td>
                    <Td>
                      <Button size="sm" variant="secondary" onClick={() => setOpen(v)}>
                        View uses
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Panel>
        ) : null}
      </LoadBoundary>
      <VoucherRecord voucher={open} onClose={() => setOpen(null)} />
    </Stack>
  );
}

function VoucherRecord({ voucher, onClose }: { voucher: VoucherDto | null; onClose: () => void }) {
  const client = useApiClient();
  const api = useMemo(() => createGrowthApi(client), [client]);
  const [rows, setRows] = useState<VoucherRedemptionDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!voucher) return;
    let live = true;
    setRows(null);
    setError(null);
    api
      .getVoucher(voucher.id)
      .then((r) => live && setRows(r.redemptions))
      .catch((e) => live && setError(apiErrorMessage(e)));
    return () => {
      live = false;
    };
  }, [api, voucher]);

  return (
    <Dialog
      open={!!voucher}
      onClose={onClose}
      title={voucher ? `${voucher.code} · ${voucher.business_name ?? "no business"}` : ""}
      actions={
        <Flex gap={8} justify="flex-end">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </Flex>
      }
    >
      <Stack gap={12}>
        {voucher ? (
          <Text role="body" color="secondary" as="p">
            {describe(voucher)}. {voucher.per_user_limit === 1 ? "Once per player" : `Up to ${voucher.per_user_limit} times per player`}
            {voucher.starts_at ? `, starts ${formatDateTime(voucher.starts_at)}` : ""}
            {voucher.expires_at ? `, ends ${formatDateTime(voucher.expires_at)}` : ""}.
          </Text>
        ) : null}
        {error ? (
          <Alert tone="danger" title="Could not load the uses">
            {error}
          </Alert>
        ) : null}
        {!error && rows === null ? (
          <Text role="body" color="secondary" as="p">
            Loading…
          </Text>
        ) : null}
        {rows && rows.length === 0 ? (
          <Text role="body" color="secondary" as="p">
            Nobody has used this code yet.
          </Text>
        ) : null}
        {rows && rows.length > 0 ? (
          <Table>
            <Thead>
              <Tr>
                <Th>When</Th>
                <Th>Player</Th>
                <Th>Booking</Th>
                <Th>Took off</Th>
                <Th>State</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={r.id}>
                  <Td>{formatDateTime(r.consumed_at ?? r.created_at)}</Td>
                  <Td>{r.user_name ?? "Unknown player"}</Td>
                  <Td>{r.booking_reference ?? "None"}</Td>
                  <Td>
                    {pesos(r.discount_amount)} of {pesos(r.list_amount)}
                  </Td>
                  <Td>
                    <StatusChip tone={REDEMPTION_TONE[r.status]}>{REDEMPTION_LABEL[r.status]}</StatusChip>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : null}
      </Stack>
    </Dialog>
  );
}
